import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";

const BRANDING_CACHE_KEY = "branding_cache_v1";

const safeParseJSON = (rawValue, fallback) => {
  try {
    if (!rawValue) return fallback;
    return JSON.parse(rawValue);
  } catch (error) {
    return fallback;
  }
};

const readBrandingCache = () => safeParseJSON(localStorage.getItem(BRANDING_CACHE_KEY), null);

const defaultBranding = {
  display_name: "EU Monitoro",
  logo_url: null,
  login_background_url: null,
  home_background_url: null,
  primary_color: "#2f855a",
  secondary_color: "#0b6530",
  accent_color: "#7dd3fc",
  default_map_mode: "image",
  default_city: null,
  allow_city_switching: false
};

const defaultContext = {
  loading: false,
  branding: defaultBranding,
  currentVisual: null,
  availableCities: [],
  selectedCity: "",
  setSelectedCity: () => {},
  refreshBranding: async () => {},
  assetUrl: (url) => url,
  canSwitchCities: false,
  isImageMode: true
};

const BrandingContext = createContext(defaultContext);

export const BrandingProvider = ({ children }) => {
  const initialCache = readBrandingCache();
  const cachedBranding = initialCache?.branding || null;
  const cachedVisual = initialCache?.currentVisual || null;
  const cachedCities = Array.isArray(initialCache?.availableCities) ? initialCache.availableCities : [];

  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ ...defaultBranding, ...(cachedBranding || {}) });
  const [currentVisual, setCurrentVisual] = useState(cachedVisual);
  const [availableCities, setAvailableCities] = useState(cachedCities);
  const [selectedCity, setSelectedCityState] = useState(localStorage.getItem("branding_city") || "");

  const user = safeParseJSON(localStorage.getItem("user"), {});
  const apiBase = api.defaults.baseURL?.replace(/\/api$/, "") || "";

  const assetUrl = useCallback(
    (url) => {
      if (!url) return null;
      if (String(url).startsWith("http")) return url;
      return `${apiBase}${url}`;
    },
    [apiBase]
  );

  const refreshBranding = useCallback(
    async (forcedCity) => {
      const currentToken = localStorage.getItem("token");
      if (!currentToken) {
        setBranding(defaultBranding);
        setCurrentVisual(null);
        setAvailableCities([]);
        return;
      }

      setLoading(true);
      try {
        const queryCity =
          forcedCity !== undefined ? forcedCity : localStorage.getItem("branding_city") || "";
        const response = await api.get("/v2/branding/me", {
          params: {
            cidade: queryCity || undefined
          }
        });

        const nextBranding = { ...defaultBranding, ...(response.data?.branding || {}) };
        const nextVisual = response.data?.current_visual || null;
        const nextCities = Array.isArray(response.data?.available_cities)
          ? response.data.available_cities
          : [];
        const resolvedCity = response.data?.current_city || queryCity || "";

        setBranding(nextBranding);
        setCurrentVisual(nextVisual);
        setAvailableCities(nextCities);
        setSelectedCityState(resolvedCity);

        if (resolvedCity) {
          localStorage.setItem("branding_city", resolvedCity);
        } else {
          localStorage.removeItem("branding_city");
        }

        localStorage.setItem(
          BRANDING_CACHE_KEY,
          JSON.stringify({
            branding: nextBranding,
            currentVisual: nextVisual,
            availableCities: nextCities,
            currentCity: resolvedCity,
            updatedAt: Date.now()
          })
        );
      } catch (error) {
        if (error?.response?.status === 401) {
          return;
        }

        const cached = readBrandingCache();
        if (cached?.branding) {
          setBranding({ ...defaultBranding, ...cached.branding });
          setCurrentVisual(cached.currentVisual || null);
          setAvailableCities(Array.isArray(cached.availableCities) ? cached.availableCities : []);
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    refreshBranding(localStorage.getItem("branding_city") || "");
  }, [refreshBranding]);

  const setSelectedCity = useCallback(
    (city) => {
      const next = city || "";
      if (next === selectedCity) {
        return;
      }
      setSelectedCityState(next);
      if (next) {
        localStorage.setItem("branding_city", next);
      } else {
        localStorage.removeItem("branding_city");
      }
      refreshBranding(next);
    },
    [refreshBranding, selectedCity]
  );

  const canSwitchCities =
    ["owner", "admin", "superadmin"].includes(String(user?.role || "").toLowerCase()) ||
    Boolean(branding.allow_city_switching) ||
    availableCities.length > 1;

  const isImageMode = useMemo(() => {
    const visualMode = String(currentVisual?.visual_mode || branding.default_map_mode || "image").toLowerCase();
    if (visualMode === "image") return true;
    if (visualMode === "google" || visualMode === "map" || visualMode === "maps") return false;
    const hasImage = Boolean(
      currentVisual?.background_image_url || currentVisual?.home_image_url || branding.home_background_url
    );
    return hasImage;
  }, [
    branding.default_map_mode,
    branding.home_background_url,
    currentVisual?.background_image_url,
    currentVisual?.home_image_url,
    currentVisual?.visual_mode
  ]);

  const value = useMemo(
    () => ({
      loading,
      branding,
      currentVisual,
      availableCities,
      selectedCity,
      setSelectedCity,
      refreshBranding,
      assetUrl,
      canSwitchCities,
      isImageMode
    }),
    [
      loading,
      branding,
      currentVisual,
      availableCities,
      selectedCity,
      setSelectedCity,
      refreshBranding,
      assetUrl,
      canSwitchCities,
      isImageMode
    ]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext);
